import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router';
import {
  Accordion, AccordionDetails, AccordionSummary, AppBar, Box, Button, Card, CardContent, Chip, CircularProgress, Container,
  IconButton, Link, ListSubheader, Menu, MenuItem, Divider, Paper, Stack, Toolbar, Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { alpha, keyframes } from '@mui/material/styles';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import MicIcon from '@mui/icons-material/Mic';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import axios from 'axios';
import OpenJobsCount from './OpenJobsCount';
import Footer from './Footer';
import ThemeModeToggle from './ThemeModeToggle';
import CountrySelect from './CountrySelect';
import { CHARGED_IN_USD, useMarket } from '../lib/market';
import { useAuth } from '../AuthContext';
import { landingPathFor } from '../usePermissions';
import useInView from '../useInView';
import CareerWheel from './CareerWheel';
import PlanComparison from './PlanComparison';
import { dataflixPalette } from '../theme/dataflix';
import { brand } from '../brand';
import Ituka from './Ituka';
import { LANDING_GROUPS } from '../landingPages';

/**
 * The page anybody lands on at the front door.
 *
 * It used to be the sign-in form: "/" was inside the protected block, so a
 * stranger arriving at the address was bounced to /login and had to work out
 * what they were being asked to sign in to. This says what RoRi is, who it is
 * for and what it costs, before asking for anything.
 *
 * Signed in, it is not a page at all — it redirects to wherever that role
 * starts, which is what "/" did before.
 */

/** `$10`, `$10.50`, `$0` — trailing `.00` dropped because prices here are round. */
const money = (cents) => `$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;

/**
 * Pricing is only pricing if it has numbers in it.
 *
 * A 200 carrying the wrong shape is not hypothetical — a proxy, a stale build
 * or a renamed field all produce one — and without this the page renders
 * `$NaN`, which is worse than saying nothing about the price at all.
 */
const usablePricing = (data) =>
  ['seatPriceCents', 'interviewPriceCents', 'premiumPriceCents']
    .every((field) => Number.isFinite(data?.[field]))
    ? data
    : null;

/** What Interview as a Service costs, or null when the two prices did not arrive: no price rather than a guessed one. */
const iasPricesOf = (data) => (
  Number.isFinite(data?.iasBasePriceCents) && Number.isFinite(data?.iasScreenPriceCents)
    ? { baseCents: data.iasBasePriceCents, screenCents: data.iasScreenPriceCents }
    : null
);

/**
 * What RoRi does, for each of the two kinds of person who sign up.
 *
 * Every line here is something the application does today, in the terms its own
 * docs use, and copy must not get ahead of that. Where a feature is part of
 * Premium it says so (`premium: true`), because a tile that sells a thing the
 * plan below then withholds is a promise broken one click later.
 *
 * There used to be three groups. "Everyday organizers" sold a General User
 * account, which signup no longer offers: an individual registers as a student
 * or a job seeker and nothing else. The tasks, notes, calendar and sharing it
 * listed come with a Candidate account, so they are a tile here, under the
 * people who can actually sign up for them. The account type still exists for
 * those who have one; this page simply cannot sell it.
 *
 * `tone` names a palette colour, never a hex. The operator's chosen theme
 * (Administration, Appearance) decides what colour that is, so the page is
 * colourful in whatever theme is selected and never off it.
 */
export const SEEKERS = [
  { icon: DescriptionOutlinedIcon, tone: 'primary', title: 'Build your profile',
    text: 'Write your resume here or upload one. Download it as a PDF, and publish a profile page only if you choose to.' },
  { icon: TravelExploreIcon, tone: 'info', title: 'Browse open jobs',
    text: 'Openings posted here and collected from company career pages. Paste a link to track any job you find elsewhere.' },
  { icon: AutoAwesomeIcon, tone: 'secondary', premium: true, title: 'Match jobs',
    text: 'See how you match a job: a score, the reasons for it, and the skills you show and the ones you are missing. Strengthen your resume for one job, reworded from your own words and never added to, and read it on a role fit radar that employers never see.' },
  { icon: AssignmentTurnedInOutlinedIcon, tone: 'success', title: 'Apply and track jobs',
    text: `Apply, and keep every application and where it stands in one place. When an employer sends an offer letter through ${brand.name}, you read it and sign it in the browser.` },
  { icon: EventOutlinedIcon, tone: 'warning', title: 'Events',
    text: 'Career fairs, networking evenings and info sessions. Post one, or save the ones you plan to go to.' },
  { icon: ContactsOutlinedIcon, tone: 'primary', title: 'Network',
    text: 'Keep the people you are building a career through, and when you said you would speak to them next.' },
  { icon: SchoolOutlinedIcon, tone: 'secondary', title: 'Upskill',
    text: 'Course and certification suggestions drawn from your resume and career goals, chosen only from a catalogue of real ones. On Premium, rehearse an interview for a specific job and read the feedback afterwards.' },
  { icon: NoteAltOutlinedIcon, tone: 'info', title: 'Take notes',
    text: 'Keep notes in categories of your own, and share a note with somebody else.' },
  { icon: ChecklistRtlIcon, tone: 'success', title: 'Personal tasks',
    text: 'Lists for the week around your search, so nothing slips.' },
  { icon: GroupsOutlinedIcon, tone: 'warning', title: 'Group tasks',
    text: 'Make a group, invite people by email, and give them tasks. A study group, a job-search buddy, a family.' },
  { icon: CalendarMonthOutlinedIcon, tone: 'primary', title: 'Manage your calendar',
    text: `Connect Google or Microsoft, or keep your events in ${brand.name}. Meetings you book through ${brand.name} land on it.` },
  { icon: MicIcon, tone: 'secondary', title: `Ask ${brand.name}, by chat or voice`,
    text: 'Add a task, book a meeting, write up a note or ask what is on today. Type it in chat, or on Premium, say it out loud.' },
];

/**
 * What sits across every step of the hiring process rather than in one of them. It is a line under the nine boxes,
 * not a tenth box: the boxes are the steps.
 */
const HIRING_ACROSS = 'Across every step: reports on the process, permissions by role, and a history of who did what.';

/**
 * How a requisition becomes a signed offer, for the hiring organization only: nine steps, three led by a person
 * and six done by the AI agent.
 *
 * It is a picture of who is in control, so the people's steps sit in a tile above and the agent's in a tile
 * below, each in the column of its place in the sequence, joined by one numbered line between them. The people
 * decide (open the requisition, approve it, interview and select); the agent does the work in between.
 *
 * Job seekers are shown no process on purpose: theirs is not a sequence the product drives. This is a marketing
 * picture and changes nothing about how the product behaves: in the product the offer letter, for example, still
 * takes an approval before it is sent, which the last step says.
 */
export const HIRING_PROCESS = [
  { lane: 'human', title: 'Open job requisition', text: 'You describe the role and who you need' },
  { lane: 'agent', title: 'Create job description', text: 'Written from your details. You can edit it or write it again' },
  { lane: 'human', title: 'Approve', text: 'You sign off before the job opens' },
  { lane: 'agent', title: 'Publish', text: 'Your careers page, and Job Boards if you choose' },
  { lane: 'agent', title: 'Screen resumes', text: 'Every resume read against your bar' },
  { lane: 'agent', title: 'Voice interviews', text: 'Candidates who clear the bar are interviewed by voice and scored' },
  { lane: 'agent', title: 'Schedule interviews', text: 'For selected candidates, with each interviewer you choose' },
  { lane: 'human', title: 'Interview and select', text: 'You meet the shortlisted candidates, in as many rounds as you choose, and decide' },
  { lane: 'agent', title: 'Send offer letter', text: 'Built from your template and sent for e-signature once approved' },
];

/**
 * What RoRi does at each of the nine steps, one entry for each, in the same order as `HIRING_PROCESS`.
 *
 * These are the boxes under the picture. Each is a thing the application does today, said in the terms its own docs
 * use, and a feature appears under the step it belongs to rather than in a list of its own. A test pins that there is
 * one entry for each step.
 */
const STEP_FEATURES = [
  { lead: 'Start a job by describing the role.', points: [
    'A guided flow from the details to an approved job',
    'Say what the role needs in your own words, and the description is written from it',
    'Saved as you go, so you can come back tomorrow',
    'Clone an earlier requisition to start from it',
  ] },
  { lead: `${brand.name} writes the description for you to shape.`, points: [
    'AI-written from your details: regenerate it, edit it, and preview it as a candidate will read it',
    'Your organization\'s standard sections are added to every description',
    'Role fit radar: decide what the role needs before anyone applies',
  ] },
  { lead: 'Nothing opens until the right people agree.', points: [
    'A job is approved before it opens, by the chain you set',
    'See who has approved and who is still to',
    'Approvers can ask for changes, and the history is kept',
  ] },
  { lead: 'Put the job where people will find it.', points: [
    'Open jobs appear on your own careers page',
    'List them on Google for Jobs, which stays off until you turn it on',
    'Bring in staffing vendors on a role: they submit candidates and see only their own work',
  ] },
  { lead: 'Every application is read against the role.', points: [
    'You set the match threshold that earns a voice interview',
    'Applicants, vendor submissions and sourced candidates in one list, each marked with where it came from',
    'Each resume is read against your role fit radar',
  ] },
  { lead: 'An interview written for each job.', points: [
    'Scored from what the candidate said, with a quote behind every score',
    'You set the interview score that earns a meeting',
    'Read the transcript and play back the recording',
  ] },
  { lead: 'Candidates are offered times without the back and forth.', points: [
    'Those who clear both bars are emailed times to choose from',
    'One round or several, each with the interviewer you choose',
    `Interviewers can be teammates, or people outside ${brand.name} who need no account`,
    'Reschedule or cancel, and a vendor is told what became of it',
  ] },
  { lead: 'This is where people decide.', points: [
    'Interviewers record a recommendation: advance, hold or reject',
    'It is advice. You decide who goes on to the next round',
    'Record the outcome you reach: only the outcomes you allow lead to an offer',
  ] },
  { lead: 'From decision to signed offer.', points: [
    'Written from your template, then sent for approval',
    'The candidate signs online, and your named signer countersigns',
    'A signed letter is kept unchanged as a record',
    'A vendor is told the status of an offer, never its terms',
  ] },
];

/** The mascot, sized like an icon (by `fontSize`), because the agent doing the work is the assistant. */
const AgentIcon = ({ sx, ...props }) => (
  <Ituka size={sx?.fontSize || 24} still {...props} sx={{ animation: sx?.animation }} />
);

/**
 * How the process picture moves.
 *
 * It plays once the picture scrolls into view: the line is drawn left to right, and each step appears as the line
 * reaches it, a person's rising out of the line and an agent's dropping away from it. Then a token travels the line
 * from the first step to the last, over and over, lighting each step as it passes: a firm ring for a person, a soft glow and a
 * nudge of the RoRi mark for the agent. It is a picture of the hand-off, a person starting, the agent working, a
 * person checking.
 *
 * Every time is derived here from one place so the token and the lights cannot drift apart. Only `transform` and
 * `opacity` move, apart from the token's position along the line. With reduced motion asked for, none of it runs and
 * the picture is simply drawn.
 *
 * On a phone (anything narrower than the wide layout) there is no line, so no token: the steps fade up in turn, and
 * then the boxes themselves light one after another, top to bottom, with the same light as on a wide screen and a
 * small pop of the numbered chip. A phone shows the whole list at once, so it is quicker (a second a step) than the
 * wide picture, and rests a little between rounds.
 */
const STAGGER = 0.35;
const TRAVEL = 6;
const CYCLE = 9;
const PEAK = 6;
const PHONE = { stagger: 0.1, step: 1, cycle: 12 };

export const processTimings = (count) => {
  const spine = count * STAGGER;
  const loopStart = spine + 0.8;
  const peak = (PEAK / 100) * CYCLE;
  return {
    spine, travel: TRAVEL, cycle: CYCLE, loopStart,
    // A step appears as the line reaches the middle of its column.
    reveal: (index) => Number((((index + 0.5) / count) * spine).toFixed(3)),
    // The step's light peaks at the instant the token is over it.
    lit: (index) => Number((loopStart + (count > 1 ? index / (count - 1) : 0) * TRAVEL - peak).toFixed(3)),
    // The list on a phone: no line to travel, so each box lights in its turn.
    phone: {
      cycle: PHONE.cycle,
      reveal: (index) => Number((index * PHONE.stagger).toFixed(3)),
      lit: (index) => Number((count * PHONE.stagger + 0.8 + index * PHONE.step - (PEAK / 100) * PHONE.cycle).toFixed(3)),
    },
  };
};

const drawLine = keyframes`from { transform: scaleX(0); } to { transform: scaleX(1); }`;
const rise = keyframes`from { opacity: 0; transform: translateY(var(--rise)); } to { opacity: 1; transform: none; }`;
const pop = keyframes`0% { transform: scale(0); } 70% { transform: scale(1.18); } 100% { transform: scale(1); }`;
const growStem = keyframes`from { transform: scaleY(0); } to { transform: scaleY(1); }`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const lightUp = keyframes`0% { opacity: 0; } ${PEAK}% { opacity: 1; } ${PEAK * 2.5}% { opacity: 0; } 100% { opacity: 0; }`;
const bump = keyframes`
  0% { transform: none; }
  ${PEAK}% { transform: scale(1.3); }
  ${PEAK * 2.5}% { transform: none; }
  100% { transform: none; }
`;
const nudge = keyframes`
  0% { transform: none; }
  ${PEAK - 3}% { transform: rotate(-14deg) scale(1.2); }
  ${PEAK}% { transform: rotate(10deg) scale(1.2); }
  ${PEAK * 2}% { transform: none; }
  100% { transform: none; }
`;
const carry = keyframes`
  0% { left: 0; opacity: 0; }
  3% { opacity: 1; }
  ${((TRAVEL / CYCLE) * 100).toFixed(3)}% { left: 100%; opacity: 1; }
  ${(((TRAVEL + 0.3) / CYCLE) * 100).toFixed(3)}% { left: 100%; opacity: 0; }
  100% { left: 100%; opacity: 0; }
`;


/** Who the lanes belong to. The people's lane is the stronger colour: they are the ones in control. */
const LANES = {
  human: { tone: 'primary', icon: PersonOutlineIcon, label: 'You', heading: 'You are in control'},
  agent: { tone: 'secondary', icon: AgentIcon, label: 'AI agent', heading: 'The AI agent does the work'},
};

/**
 * What Premium adds to job matching, in the words the plan card, the comparison table and the FAQ all use. The free list of
 * strongest jobs and sorting by best match are in Free, so the paid part is named here and nowhere called just "job matches".
 */
export const PREMIUM_MATCHING = 'match scores with reasons, link matches and the role fit radar';

/**
 * What each plan is, with the price filled in once pricing arrives.
 *
 * `price` is a function rather than a string so a plan can say nothing at all
 * about money when the pricing call fails — a plan with no price is honest,
 * a plan with a wrong price is not.
 */
export const PLANS = (pricing, price = money) => [
  {
    name: 'Free',
    group: 'seekers',
    price: () => price(0),
    cadence: 'forever',
    summary: 'For one person. No card needed. Your profile, job search, a list of your strongest job matches, network, events, courses, tasks and chat.',
    action: { label: 'Create a free account', to: '/signup?plan=free' },
  },
  {
    name: 'Premium',
    group: 'seekers',
    price: () => (pricing ? price(pricing.premiumPriceCents) : null),
    cadence: 'a month',
    summary: `Everything in Free, plus your job search worked through for you: ${PREMIUM_MATCHING}, practice interviews, people to talk to, and ${brand.name} by voice. Cancel whenever you like.`,
    highlight: true,
    action: { label: 'Start with Premium', to: '/signup?plan=premium' },
  },
  {
    name: 'Enterprise',
    group: 'hiring',
    price: () => (pricing ? price(pricing.seatPriceCents) : null),
    cadence: 'per user, a month',
    summary: pricing
      ? `Plus ${price(pricing.interviewPriceCents)} for each candidate interview.`
      : 'Billed per user, plus a charge for each candidate interview.',
    points: [
      'Requisitions, approvals and your own careers page',
      'Resume screening and AI voice interviews',
      'Vendor sourcing and candidate submissions',
      'Roles, permissions and audit history',
    ],
    // With self-serve payment off a signup queues for an operator rather than
    // taking a card, so the button must not imply instant access.
    action: { label: pricing && !pricing.selfServeEnabled ? 'Request access' : 'Set up your organization', to: '/signup?plan=enterprise' },
  },
  {
    name: 'Interview as a Service',
    group: 'hiring',
    price: () => (iasPricesOf(pricing) ? price(iasPricesOf(pricing).baseCents) : null),
    cadence: 'a month',
    summary: iasPricesOf(pricing)
      ? `Plus ${price(iasPricesOf(pricing).screenCents)} for each completed interview. Keep your own hiring system and add voice screening under your own name.`
      : 'Plus a charge for each completed interview. Keep your own hiring system and add voice screening under your own name.',
    points: [
      'Connect Greenhouse, or call the API from any system that can send a web request',
      'Candidates meet your company, not ours',
      'A resume evaluation and a voice interview for each candidate you send',
      'Try it first with a free test key',
    ],
    // An employer who signs up is let in at once only when an operator has switched that on; until then the account waits for approval,
    // so the button must not promise more than it does.
    action: { label: pricing?.iasSignupOpen === true ? 'Set up screening' : 'Request access', to: '/employers/signup' },
  },
];

/**
 * Who each plan is for. The plans are shown in these two groups, in the order of the sections above them and in
 * their colours (the same `tone` as the audience's section and as its choice at the top), so a visitor can see at
 * once that Free and Premium are for them if they are a student or job seeker, and Enterprise if they hire.
 * `plansId` is where the link at the end of that audience's section lands.
 */
const PLAN_GROUPS = [
  {
    id: 'seekers',
    plansId: 'plans-seekers',
    tone: 'secondary',
    icon: SchoolOutlinedIcon,
    title: 'For students and job seekers',
    text: 'Your Career Wheel, free to start',
    note: `Personal accounts are free. Chat with ${brand.name} stays free on both plans. Premium is billed monthly, and you can cancel whenever you like.`,
  },
  {
    id: 'hiring',
    plansId: 'plans-hiring',
    tone: 'success',
    icon: BusinessCenterOutlinedIcon,
    title: 'For hiring teams and agencies',
    text: `${brand.name}’s AI agent, from requisition to signed offer`,
    note: 'Enterprise is billed per user each month, plus a charge for each candidate interview. Interview as a Service is billed per account each month, plus a charge for each completed interview. Screening and interview results are produced with AI and can be inaccurate — hiring decisions stay with the organization.',
  },
];

/**
 * The brand in the top bar. The mark was 36px, pale on a near-white bar, and the wordmark 20px: it read as
 * an afterthought. Now the mark sits on a tile washed in the theme's own colours with a ring, so a pale logo
 * still stands out in any theme and in dark mode, and the wordmark is large and set in the same two colours.
 * The logo in the bar is Ituka, and so is the picture in the hero and the agent's mark in the process: one mascot.
 */
export const Brand = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 1.75 }, minWidth: 0 }}>
    <Box
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        width: { xs: 44, sm: 56 }, height: { xs: 44, sm: 56 }, borderRadius: 2.5,
        background: (theme) => `linear-gradient(135deg, ${alpha(toneOf(theme, 'primary'), 0.22)} 0%, ${alpha(toneOf(theme, 'secondary'), 0.2)} 100%)`,
        boxShadow: (theme) => `inset 0 0 0 1.5px ${alpha(toneOf(theme, 'primary'), 0.45)}, 0 4px 12px ${alpha(toneOf(theme, 'primary'), 0.22)}`,
      }}
    >
      <Ituka size={50} still label={brand.name} sx={{ height: { xs: 40, sm: 50 }, width: { xs: 40, sm: 50 } }} />
    </Box>
    <Box sx={{ lineHeight: 1, minWidth: 0 }}>
      <Typography
        variant="h5"
        component="span"
        sx={{
          display: 'block', fontSize: { xs: '1.6rem', sm: '1.9rem' }, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05,
          color: (theme) => toneOf(theme, 'primary'),
          // Where gradient text is supported the wordmark takes both colours; where not, it keeps the first.
          '@supports (background-clip: text) or (-webkit-background-clip: text)': {
            backgroundImage: (theme) => `linear-gradient(90deg, ${toneOf(theme, 'primary')}, ${toneOf(theme, 'secondary')})`,
            WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
          },
        }}
      >
        {brand.name}
      </Typography>
      {/* At 375px the two words wrapped and pushed the bar to three lines. The
          wordmark alone is enough there; the hero says what it is anyway. */}
      <Typography
        variant="caption"
        sx={{ color: 'text.primary', opacity: 0.75, fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}
      >
        Personal Hiring &amp; Career Assistant
      </Typography>
    </Box>
  </Box>
);

/** A palette colour by name. Never a hex: the operator's theme decides what "primary" is. */
const toneOf = (theme, name) => (theme.palette[name] || theme.palette.primary).main;

/** A heading and a one-line explanation. `id` lets the bar link straight to it. */
const Points = ({ points }) => (
  <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1, color: 'text.secondary', lineHeight: 1.6 }, '& li::marker': { color: 'primary.main' } }}>
    {points.map((point) => <li key={point}>{point}</li>)}
  </Box>
);

/** One feature: a coloured icon, a name and a sentence, with a rule of the same colour across the top. */
const FeatureTile = ({ feature }) => {
  const Icon = feature.icon;
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderTop: (theme) => `4px solid ${toneOf(theme, feature.tone)}`,
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.25 }}>
          <Box
            aria-hidden
            sx={{
              width: 42, height: 42, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center',
              bgcolor: (theme) => alpha(toneOf(theme, feature.tone), 0.14),
              color: (theme) => toneOf(theme, feature.tone),
            }}
          >
            <Icon />
          </Box>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, lineHeight: 1.25 }}>{feature.title}</Typography>
        </Stack>
        <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>{feature.text}</Typography>
        {feature.premium && <Chip size="small" variant="outlined" color="primary" label="On Premium" sx={{ mt: 1.5 }} />}
      </CardContent>
    </Card>
  );
};

/**
 * One step of the hiring process, as a box: its number and name as in the picture above it, who leads it, and what
 * RoRi does there. Tinted by who leads the step, like the picture, so the two read as one thing.
 */
const StepBox = ({ step, number, features }) => {
  const lane = LANES[step.lane];
  return (
    <Card
      variant="outlined"
      data-step={number}
      data-lane={step.lane}
      sx={{
        height: '100%',
        borderTop: (theme) => `4px solid ${toneOf(theme, lane.tone)}`,
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <Box
            aria-hidden
            sx={{
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', fontWeight: 700,
              color: (theme) => theme.palette.getContrastText(toneOf(theme, lane.tone)), bgcolor: (theme) => toneOf(theme, lane.tone),
            }}
          >
            {number}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, lineHeight: 1.25 }}>{step.title}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: (theme) => toneOf(theme, lane.tone) }}>
              {step.lane === 'human' ? 'You decide' : 'AI agent'}
            </Typography>
          </Box>
        </Stack>
        <Typography color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>{features.lead}</Typography>
        <Points points={features.points} />
      </CardContent>
    </Card>
  );
};

/**
 * A band across the page that names one audience, in a colour of its own.
 *
 * `id` is where the two choices at the top of the page land, so the heading can take focus (it is not otherwise a
 * place anybody tabs to) and the page does not leave a keyboard or screen-reader user at the top of the page they
 * have just been sent away from.
 */
/**
 * The banner over a part of the page: a wash of the part's colour with its name in large type. The two audiences'
 * sections and the plans all use it, so the three big headings on the page look alike. `summary` is optional.
 */
const BandHeader = ({ id, tone, title, summary }) => (
  <Box
    sx={{
      p: { xs: 2.5, md: 3.5 }, borderRadius: 3, mb: 3,
      background: (theme) => `linear-gradient(120deg, ${alpha(toneOf(theme, tone), 0.18)}, ${alpha(toneOf(theme, tone), 0.05)})`,
      border: (theme) => `1px solid ${alpha(toneOf(theme, tone), 0.3)}`,
    }}
  >
    <Typography id={`${id}-heading`} tabIndex={-1} variant="h5" component="h2" sx={{ fontWeight: 700, outline: 'none' }}>{title}</Typography>
    {summary && <Typography color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>{summary}</Typography>}
  </Box>
);

const AudienceBand = ({ id, tone, title, summary, before, after, children }) => (
  <Box id={id} component="section" aria-labelledby={`${id}-heading`} sx={{ scrollMarginTop: 16, mb: { xs: 3, md: 5 } }}>
    <BandHeader id={id} tone={tone} title={title} summary={summary} />
    {before}
    <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
      {children}
    </Box>
    {after}
  </Box>
);

/**
 * One of the two choices at the top of the page. A real link to the part of the page for that audience, so it works
 * with no script; with script it scrolls smoothly (not for somebody who asked for less motion), moves focus to the
 * heading it lands on, and puts the place in the address so the back button and a copied link both come back to it.
 */
const jumpTo = (event, id) => {
  const target = document.getElementById(id);
  if (!target) return;
  event.preventDefault();
  const calm = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (typeof target.scrollIntoView === 'function') target.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
  target.querySelector('h2, h3')?.focus({ preventScroll: true });
  if (window.history?.replaceState) window.history.replaceState(null, '', `#${id}`);
};

/**
 * A link from the end of an audience's section to the plans that are theirs. Like the choices at the top it is a real
 * link to the place, so it works with no script, and with script it goes by `jumpTo`.
 */
const PlansLink = ({ id, tone, label }) => (
  <Button
    component="a"
    href={`#${id}`}
    onClick={(event) => jumpTo(event, id)}
    data-plans-link={id}
    endIcon={<ArrowForwardIcon sx={{ transform: 'rotate(90deg)' }} />}
    sx={{ mt: 2.5, fontWeight: 700, textTransform: 'none', color: (theme) => toneOf(theme, tone) }}
  >
    {label}
  </Button>
);

/**
 * Where the hero's two parts sit, left to right on a wide screen: the panel for this stream, then RoRi. Also their order in the
 * page, so a keyboard or a screen reader meets the panel first. On a phone RoRi comes first, under the heading, then the panel.
 */
export const HERO_AREAS = ['panel', 'rori'];

/**
 * What RoRi is for each of the two audiences, written from what the product does today. The hiring side never says
 * chat or voice: hiring staff do not have the assistant, only the work the agent does between their decisions.
 */
const HERO_PANELS = {
  seekers: {
    tone: 'secondary',
    icon: SchoolOutlinedIcon,
    title: 'Student or job seeker',
    lead: 'Find roles, track every application, and keep the rest of your week in order.',
    is: `${brand.name} is an assistant you can chat with or talk to.`,
    points: [
      'Build your profile and browse open jobs',
      'Apply, track every application and sign your offer letter',
      'Tasks, notes, a calendar and course and certification suggestions',
    ],
  },
  hiring: {
    tone: 'success',
    icon: BusinessCenterOutlinedIcon,
    title: 'Looking to hire',
    lead: 'Run hiring from requisition to signed offer, with an AI agent doing the work and you making the decisions.',
    is: `${brand.name} is an AI agent that does the work between your decisions.`,
    points: [
      'Writes the job description and publishes the posting',
      'Screens every resume and interviews those who clear the bar by voice',
      'Schedules interviews and prepares the offer letter for your approval',
    ],
  },
};

/** The line under the heading, for each stream. */
const HERO_LINE = {
  seekers: `${brand.name} is the personal AI assistant for students and job seekers.`,
  hiring: `${brand.name} is the AI agent for hiring teams: it does the work between your decisions, and shows its evidence.`,
};

/** The way in from the hero. The plan travels in the address, so sign-up does not ask for it again. */
const HERO_ACTION = {
  seekers: { label: 'Create a free account', to: '/signup?plan=free' },
  hiring: { label: 'Set up your organization', to: '/signup?plan=enterprise' },
};

/**
 * What a hiring team that has to answer to somebody looks for, each one a thing the product does today and a page that says more. The
 * wording is plain on purpose: nothing here is a certification, a customer or a number, and the Security page says what is not in place.
 */
export const CONTROL_TILES = [
  { title: 'Approvals you set', text: 'An approval chain signs each job off before it opens, and every decision is recorded with who and when.', to: '/requisition-approval-workflow' },
  { title: 'Evidence on every score', text: 'Each score comes with the quotes behind it. A score never closes an application on its own.', to: '/responsible-ai' },
  { title: 'Roles and an audit trail', text: 'Roles decide who sees what, and sign-ins, role changes and offer decisions are recorded.', to: '/security' },
  { title: 'Recordings with consent', text: 'Video is optional, shown only to the people who review the interview, and deleted on a schedule.', to: '/security' },
  { title: 'Vendors see status only', text: 'Staffing partners submit candidates and never see your thresholds, scores or offer terms.', to: '/vendor-submittal-portal' },
  { title: 'Your name and address', text: 'Run voice screening under your own company name and logo, beside the hiring system you already use.', to: '/interview-as-a-service' },
];

/**
 * For a hiring team that already has a hiring system and wants only the voice screening: what happens, in three steps, and the two ways
 * in. Written from what the product does today. Greenhouse is the one connector built; every other system uses the API, and nothing
 * here names another system or says it has been connected.
 */
export const EXISTING_SYSTEM = {
  title: 'Already have a hiring system? Keep it.',
  summary: `${brand.name} adds voice screening to the system you already use. Send it a candidate and it reads the resume, interviews them by voice under your company's name, and gives you the result.`,
  steps: [
    { title: 'Send a candidate', text: 'Your system sends the job description, the candidate and the resume in one call, or Greenhouse sends them for you.' },
    { title: 'We interview by voice', text: `${brand.name} reads the resume against the job, invites the candidate under your company's name and address, and interviews them by voice.` },
    { title: 'Read the result', text: 'You are told by a signed call, or you ask. You get the resume evaluation, the voice screen and the evidence behind each score, and your team decides.' },
  ],
  connects: ['Greenhouse', 'Any system that can send a web request'],
};

const floatUp = keyframes`0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); }`;

/**
 * The panel beside RoRi in the hero: what the product is for this stream, in terms of what it does today. Its name is its title;
 * what it says underneath describes it. `children` is what is under the list (the ways to use it, or the way in).
 */
const AudiencePanel = ({ id, children }) => {
  const { tone, icon: Icon, title, lead, is, points } = HERO_PANELS[id];
  return (
    <Paper
      variant="outlined"
      data-choice={id}
      data-area="panel"
      aria-labelledby={`hero-${id}-title`}
      aria-describedby={`hero-${id}-lead`}
      sx={{
        gridArea: 'panel', display: 'flex', flexDirection: 'column', gap: 1.5, p: { xs: 2, md: 3 }, color: 'inherit',
        height: '100%', borderWidth: 2, borderColor: (theme) => alpha(toneOf(theme, tone), 0.55),
        bgcolor: (theme) => alpha(toneOf(theme, tone), 0.08),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          aria-hidden
          sx={{
            width: 44, height: 44, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center',
            bgcolor: (theme) => alpha(toneOf(theme, tone), 0.18), color: (theme) => toneOf(theme, tone),
          }}
        >
          <Icon />
        </Box>
        <Typography id={`hero-${id}-title`} variant="h6" component="p" sx={{ fontWeight: 700, lineHeight: 1.25, flexGrow: 1 }}>{title}</Typography>
      </Stack>
      <Typography id={`hero-${id}-lead`} variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>{lead}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.6 }}>{is}</Typography>
      <Box
        component="ul"
        sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75, color: 'text.secondary', lineHeight: 1.5, fontSize: '0.9rem' }, '& li::marker': { color: (theme) => toneOf(theme, tone) } }}
      >
        {points.map((point) => <li key={point}>{point}</li>)}
      </Box>
      {children}
    </Paper>
  );
};

/**
 * RoRi in the middle of the hero, on a soft wash of the theme's colours, with a dashed line out to each side. The
 * lines are decoration and only on a wide screen, where there is a side to join. The image floats a little unless
 * somebody asked for less motion.
 */
const RoriStage = () => (
  <Box
    data-area="rori"
    sx={{ gridArea: 'rori', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: { xs: 0, md: 360 } }}
  >
    <Box
      aria-hidden
      sx={{
        position: 'absolute', inset: { xs: '5% 10%', md: '0 -4%' }, borderRadius: '50%', filter: 'blur(6px)',
        background: (theme) => `radial-gradient(circle at 50% 45%, ${alpha(toneOf(theme, 'primary'), 0.22)} 0%, ${alpha(toneOf(theme, 'secondary'), 0.14)} 45%, transparent 72%)`,
      }}
    />
    {['left', 'right'].map((side) => (
      <Box
        key={side}
        aria-hidden
        sx={{
          display: { xs: 'none', md: 'block' }, position: 'absolute', top: '50%', [side]: -28, width: 28, height: 0,
          borderTop: (theme) => `2px dashed ${alpha(toneOf(theme, side === 'left' ? 'secondary' : 'success'), 0.7)}`,
        }}
      />
    ))}
    <Box
      sx={{
        position: 'relative', display: 'flex', width: '100%', maxWidth: { xs: 220, md: 340 }, justifyContent: 'center',
        filter: 'drop-shadow(0 18px 28px rgba(20,33,61,0.18))',
        animation: `${floatUp} 7s ease-in-out infinite`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Ituka label={`${brand.name}, the assistant`} size={300} still sx={{ width: '100%', height: 'auto', maxWidth: { xs: 220, md: 300 } }} />
    </Box>
  </Box>
);

/**
 * The two streams of the front door. Each is its own page with its own address, under one bar and one footer: a student or job
 * seeker is shown the job seeker's page, a hiring team the hiring page. `/` shows the one the visitor looked at last (job seekers
 * for somebody new), so there is no page that talks to both at once.
 */
export const AUDIENCES = {
  seekers: { id: 'seekers', path: '/for-job-seekers', label: 'For job seekers', tone: 'secondary' },
  hiring: { id: 'hiring', path: '/for-hiring-teams', label: 'For hiring teams', tone: 'success' },
};
const AUDIENCE_IDS = ['seekers', 'hiring'];
const AUDIENCE_KEY = 'ituka.audience';

const rememberedAudience = () => {
  try {
    const stored = window.localStorage.getItem(AUDIENCE_KEY);
    return AUDIENCES[stored] ? stored : null;
  } catch {
    return null;
  }
};
const rememberAudience = (id) => {
  try { window.localStorage.setItem(AUDIENCE_KEY, id); } catch { /* storage blocked: "/" opens on job seekers */ }
};

/** Which questions are answered on which stream: the first three are a job seeker's, the rest a hiring team's (what it does and costs, then the ones a security reviewer asks). */
const FAQ_STREAM = { seekers: [0, 1, 2], hiring: [3, 4, 5, 6, 7, 8] };
/**
 * What people ask before they sign up, answered from what the product does. Prices come from the server like every
 * other price on this page, and are left out of the sentence when they did not load rather than guessed at.
 */
export const FAQS = (pricing, price = money) => [
  {
    q: `Is ${brand.name} free?`,
    a: `Yes, for students and job seekers. Your profile, job search, a list of your strongest job matches, applications, events, network, tasks, notes, calendar, course and certification suggestions and chat are free. Premium${pricing ? ` (${price(pricing.premiumPriceCents)} a month)` : ''} adds ${PREMIUM_MATCHING}, strengthening your resume for a job, practice interviews, people to talk to, and ${brand.name} by voice.`,
  },
  {
    q: 'Do I need a card to sign up?',
    a: 'No. A job seeker or student account is free and needs no card. Premium is a separate subscription you can add later and cancel whenever you like.',
  },
  {
    q: 'Who can see my profile?',
    a: 'Only you, until you choose. Publishing a profile page for people you pick, and being findable in hiring organizations\' searches, are two separate choices. You can do either, both or neither.',
  },
  {
    q: 'Does a score ever reject a candidate?',
    a: 'No. A score never closes an application on its own. A person on the hiring team decides, and can still offer an interview to an applicant who is below the bar, with a reason that is recorded.',
  },
  {
    q: `What does ${brand.name} do in hiring, and what do I decide?`,
    a: `You open the requisition, approve it, and interview and select. ${brand.name} writes the job description, publishes the posting, screens resumes, runs the voice interviews and schedules the next interviews. An offer letter needs an approval before it is sent.`,
  },
  {
    q: 'What does hiring cost?',
    a: `${pricing
      ? `Hiring teams pay ${price(pricing.seatPriceCents)} per user a month, and ${price(pricing.interviewPriceCents)} per candidate interview.`
      : 'Hiring teams pay per user a month, and a charge applies to every candidate interview.'}${iasPricesOf(pricing)
      ? ` If you keep your own hiring system and want only voice screening, Interview as a Service is ${price(iasPricesOf(pricing).baseCents)} a month plus ${price(iasPricesOf(pricing).screenCents)} for each completed interview.`
      : ''}`,
  },
  {
    q: `Does ${brand.name} support single sign-on?`,
    a: 'Not yet. People can sign in with a Google or Microsoft account or a password. SAML single sign-on, SCIM provisioning and multi-factor authentication are not available yet, and are scheduled for later.',
  },
  // {
  //   q: `Is ${brand.name} SOC 2 certified?`,
  //   a: `No. ${brand.name} has not completed a SOC 2 or ISO 27001 audit, and does not show badges it has not earned. What protects your data today, and what is not in place yet, is written out on the Security page.`,
  // },
  {
    q: 'Where is my data kept?',
    a: 'On Amazon Web Services in the United States (us-east-1), encrypted at rest. Interview recordings are kept in a private, encrypted store and deleted on a schedule.',
  },
  {
    q: 'Can I keep my own hiring system and add voice screening?',
    a: `Yes. Interview as a Service connects to Greenhouse, or through an API to any system that can send a web request. ${brand.name} evaluates each resume you send, interviews the candidate by voice under your company's name, and gives you the result. It never moves or rejects anyone in your system.`,
  },
];

/**
 * The hiring process as two tiles, people above and the agent below.
 *
 * One ordered list in sequence order, so it reads 1 to 9 to anything that is not looking at it. On a wide screen
 * the same items are placed on a grid: nine columns, one per step, with a person's step in the upper tile and
 * an agent's in the lower, and a numbered line between them (decorative, `aria-hidden`). On a phone there are no
 * tiles: it is the list, top to bottom, each step saying who does it.
 */
const HiringProcess = () => {
  const wide = { lg: 'grid' };
  const decorative = { role: 'presentation', 'aria-hidden': true };
  const ref = useRef(null);
  const { seen, on } = useInView(ref);
  const time = processTimings(HIRING_PROCESS.length);
  // The reveal runs once seen. The loop also stops while the picture is off screen, and while somebody points at it.
  const reveal = seen ? 'running' : 'paused';
  const loop = seen && on ? 'running' : 'paused';
  const tone = (theme, lane) => toneOf(theme, LANES[lane].tone);
  return (
    <Box
      ref={ref}
      component="ol"
      aria-label="From a requisition to a signed offer"
      data-motion={seen ? 'playing' : 'waiting'}
      sx={{
        listStyle: 'none', p: 0, m: 0, mb: 3, display: 'grid', gap: 1.25,
        gridTemplateColumns: { xs: '1fr', lg: `repeat(${HIRING_PROCESS.length}, minmax(0, 1fr))` },
        gridTemplateRows: { lg: 'auto auto 64px auto auto' },
        columnGap: { lg: 0 }, rowGap: { lg: 0 }, px: { lg: 1 },
        '& [data-reveal]': { animationPlayState: reveal },
        // `data-loop` is an element that loops itself, `data-lit` one whose light (its ::after) loops.
        '& [data-loop], & [data-lit]::after': { animationPlayState: loop },
        '&:hover [data-loop], &:hover [data-lit]::after': { animationPlayState: 'paused !important' },
        '@media (prefers-reduced-motion: reduce)': { '& *, & *::after': { animation: 'none !important', transition: 'none !important' } },
      }}
    >
      {HIRING_PROCESS.map((step, index) => {
        const lane = LANES[step.lane];
        const Icon = lane.icon;
        const human = step.lane === 'human';
        const at = time.reveal(index);
        return (
          <Box
            key={step.title}
            component="li"
            data-lane={step.lane}
            data-reveal
            data-lit
            sx={{
              position: 'relative', zIndex: 1, m: { lg: 0.75 }, p: 1.5, borderRadius: 2.5, display: 'flex', gap: 1.25,
              gridColumn: { lg: index + 1 }, gridRow: { lg: human ? 2 : 5 },
              flexDirection: { xs: 'row', lg: 'column' }, textAlign: { lg: 'center' }, alignItems: { xs: 'flex-start', lg: 'center' },
              bgcolor: (theme) => (human ? theme.palette.background.paper : alpha(toneOf(theme, lane.tone), 0.08)),
              border: (theme) => `${human ? 2 : 1}px solid ${alpha(toneOf(theme, lane.tone), human ? 0.9 : 0.4)}`,
              boxShadow: human ? 2 : 0,
              // A person's step rises out of the line, an agent's drops away from it. On a phone all of them just rise.
              '--rise': { xs: '12px', lg: human ? '10px' : '-10px' },
              animation: { xs: `${rise} 0.5s ease-out ${time.phone.reveal(index)}s backwards`, lg: `${rise} 0.5s ease-out ${(at + 0.1).toFixed(3)}s backwards` },
              transition: 'transform 160ms ease, box-shadow 160ms ease',
              '&:hover': { transform: { lg: 'translateY(-3px)' }, boxShadow: human ? 4 : 1 },
              // The light the token leaves behind: a firm ring for a person, a soft glow for the agent.
              '&::after': {
                content: '""', position: 'absolute', inset: -2, borderRadius: 'inherit', pointerEvents: 'none', opacity: 0,
                boxShadow: (theme) => (human
                  ? `0 0 0 4px ${alpha(toneOf(theme, lane.tone), 0.5)}`
                  : `0 0 26px 3px ${alpha(toneOf(theme, lane.tone), 0.55)}`),
                animation: {
                  xs: `${lightUp} ${time.phone.cycle}s ease-out ${time.phone.lit(index)}s infinite`,
                  lg: `${lightUp} ${time.cycle}s ease-out ${time.lit(index)}s infinite`,
                },
              },
            }}
          >
            {/* On a phone the number and who does the step are inside the box; wide, the line between the tiles says both. */}
            <Box
              aria-hidden
              data-loop
              sx={{
                display: { xs: 'grid', lg: 'none' }, placeItems: 'center', width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                animation: `${bump} ${time.phone.cycle}s ease-in-out ${time.phone.lit(index)}s infinite`,
                fontWeight: 700, color: (theme) => theme.palette.getContrastText(toneOf(theme, lane.tone)), bgcolor: (theme) => toneOf(theme, lane.tone),
              }}
            >
              {index + 1}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent={{ lg: 'center' }} sx={{ mb: 0.25 }}>
                <Icon
                  aria-hidden
                  {...(human ? {} : { 'data-loop': true })}
                  sx={{
                    fontSize: 20, color: (theme) => toneOf(theme, lane.tone),
                    ...(human ? {} : {
                      animation: {
                        xs: `${nudge} ${time.phone.cycle}s ease-in-out ${time.phone.lit(index)}s infinite`,
                        lg: `${nudge} ${time.cycle}s ease-in-out ${time.lit(index)}s infinite`,
                      },
                    }),
                  }}
                />
                <Typography variant="caption" sx={{ fontWeight: 700, color: (theme) => toneOf(theme, lane.tone), display: { lg: 'none' } }}>{lane.label}</Typography>
              </Stack>
              <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>{step.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, mt: 0.5, fontSize: { lg: '0.8rem' } }}>{step.text}</Typography>
            </Box>
          </Box>
        );
      })}

      {/* Everything below is picture, for wide screens only. */}
      {['human', 'agent'].map((key) => {
        const lane = LANES[key];
        const rows = key === 'human' ? '1 / 3' : '4 / 6';
        const first = key === 'human' ? 1 : 4;
        return (
          <React.Fragment key={key}>
            <Box
              component="li"
              {...decorative}
              data-reveal
              sx={{
                display: { xs: 'none', ...wide }, gridColumn: { lg: '1 / -1' }, gridRow: { lg: rows }, mx: { lg: -1 }, borderRadius: 3,
                background: (theme) => `linear-gradient(160deg, ${alpha(toneOf(theme, lane.tone), key === 'human' ? 0.2 : 0.12)}, ${alpha(toneOf(theme, lane.tone), 0.04)})`,
                border: (theme) => `1px solid ${alpha(toneOf(theme, lane.tone), 0.35)}`,
                animation: `${fadeIn} 0.6s ease-out ${key === 'human' ? 0 : 0.2}s backwards`,
              }}
            />
            <Box
              component="li"
              {...decorative}
              data-reveal
              sx={{
                display: { xs: 'none', lg: 'flex' }, gridColumn: { lg: '1 / -1' }, gridRow: { lg: first }, zIndex: 1, alignItems: 'center', gap: 1, px: 1.5, pt: 1.25, pb: 0.25,
                animation: `${fadeIn} 0.6s ease-out ${key === 'human' ? 0.1 : 0.3}s backwards`,
              }}
            >
              <lane.icon sx={{ fontSize: 30, color: (theme) => toneOf(theme, lane.tone) }} />
              <Typography sx={{ fontWeight: 700 }}>{lane.heading}</Typography>
            </Box>
          </React.Fragment>
        );
      })}

      {/* The numbered line between the tiles: each step has a stem up to a person or down to the agent. It is drawn first. */}
      <Box
        component="li"
        {...decorative}
        data-reveal
        sx={{
          display: { xs: 'none', ...wide }, gridColumn: { lg: '1 / -1' }, gridRow: { lg: 3 }, alignSelf: 'center', height: 4, borderRadius: 2, mx: { lg: 2 },
          background: (theme) => `linear-gradient(90deg, ${toneOf(theme, 'primary')}, ${toneOf(theme, 'secondary')})`,
          transformOrigin: 'left center', animation: `${drawLine} ${time.spine}s linear 0s backwards`,
        }}
      />
      {HIRING_PROCESS.map((step, index) => {
        const human = step.lane === 'human';
        const at = time.reveal(index);
        return (
          <Box
            key={`dot-${step.title}`}
            component="li"
            {...decorative}
            sx={{
              display: { xs: 'none', ...wide }, gridColumn: { lg: index + 1 }, gridRow: { lg: 3 }, position: 'relative', placeItems: 'center', zIndex: 1,
            }}
          >
            <Box
              data-reveal
              sx={{
                position: 'absolute', left: '50%', width: 3, ml: '-1.5px', bgcolor: (theme) => tone(theme, step.lane),
                ...(human ? { top: 0, bottom: '50%', transformOrigin: 'bottom' } : { top: '50%', bottom: 0, transformOrigin: 'top' }),
                animation: `${growStem} 0.35s ease-out ${(at + 0.15).toFixed(3)}s backwards`,
              }}
            />
            <Box
              data-reveal
              data-lit
              sx={{
                position: 'relative', display: 'grid', placeItems: 'center', width: 34, height: 34, borderRadius: '50%', fontWeight: 700,
                color: (theme) => theme.palette.getContrastText(tone(theme, step.lane)), bgcolor: (theme) => tone(theme, step.lane),
                border: (theme) => `3px solid ${theme.palette.background.paper}`, boxShadow: 2,
                animation: `${pop} 0.4s ease-out ${at}s backwards`,
                '&::after': {
                  content: '""', position: 'absolute', inset: -7, borderRadius: '50%', pointerEvents: 'none', opacity: 0,
                  border: (theme) => `3px solid ${alpha(tone(theme, step.lane), 0.55)}`,
                  animation: `${lightUp} ${time.cycle}s ease-out ${time.lit(index)}s infinite`,
                },
              }}
            >
              {index + 1}
            </Box>
          </Box>
        );
      })}

      {/* The token that carries the work from the first step to the last and starts again. Same columns as the dots, so it is over each as it lights. */}
      <Box
        component="li"
        {...decorative}
        sx={{ display: { xs: 'none', ...wide }, gridColumn: { lg: '1 / -1' }, gridRow: { lg: 3 }, alignSelf: 'center', height: 0, zIndex: 2, pointerEvents: 'none' }}
      >
        <Box sx={{ position: 'relative', width: `${((HIRING_PROCESS.length - 1) / HIRING_PROCESS.length) * 100}%`, ml: `${50 / HIRING_PROCESS.length}%` }}>
          <Box
            data-loop
            data-testid="process-token"
            sx={{
              position: 'absolute', left: 0, top: -9, ml: '-9px', width: 18, height: 18, borderRadius: '50%', opacity: 0,
              bgcolor: 'background.paper',
              border: (theme) => `4px solid ${toneOf(theme, 'secondary')}`,
              boxShadow: (theme) => `0 0 14px 3px ${alpha(toneOf(theme, 'secondary'), 0.6)}`,
              animation: `${carry} ${time.cycle}s linear ${time.loopStart}s infinite`,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * The menu behind the bar on a phone and a tablet, where the audience switch, Plans, FAQ and Sign in do not fit. Before it a
 * 375 px bar held the name and Get started and nothing else, so a visitor could not reach Plans, the FAQ or the product pages
 * without scrolling the whole page. It carries the same anchors and the same product pages the footer links to.
 */
const BarMenu = ({ stream }) => {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  return (
    <>
      <IconButton
        color="inherit"
        aria-label="Menu"
        aria-haspopup="menu"
        aria-expanded={anchor ? 'true' : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ display: { xs: 'inline-flex', md: 'none' } }}
      >
        <MenuIcon />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close} MenuListProps={{ 'aria-label': 'Site menu' }}>
        {AUDIENCE_IDS.map((id) => (
          <MenuItem key={id} component={RouterLink} to={AUDIENCES[id].path} selected={stream === id} onClick={close}>{AUDIENCES[id].label}</MenuItem>
        ))}
        {[['#plans', 'Plans'], ['#faq', 'FAQ']].map(([href, label]) => (
          <MenuItem key={href} component="a" href={href} onClick={close}>{label}</MenuItem>
        ))}
        <Divider />
        {LANDING_GROUPS.map((group) => [
          <ListSubheader key={`group-${group.label}`} sx={{ lineHeight: '32px' }}>{group.label}</ListSubheader>,
          ...group.links.map(([to, label]) => (
            <MenuItem key={to} component={RouterLink} to={to} onClick={close}>{label}</MenuItem>
          )),
        ])}
        <MenuItem component={RouterLink} to="/contact" onClick={close}>Contact us</MenuItem>
        <Divider />
        <MenuItem component={RouterLink} to="/login" onClick={close}>Sign in</MenuItem>
      </Menu>
    </>
  );
};

/**
 * What is coming next, said to this stream in one band under the hero: agentic video interviews. It is labelled "Coming soon" and
 * promises nothing that is not already how the product works: a person makes every hiring decision, and what is built is a structured
 * interview and a report. The hiring side never says chat or voice (see HERO_PANELS).
 */
const COMING_SOON = {
  title: 'Agentic video interviews',
  lead: `${brand.name} is learning to meet people on video.`,
  parts: [
    {
      id: 'seekers', tone: 'secondary', icon: SchoolOutlinedIcon, who: 'For job seekers',
      text: 'Practise with an AI interviewer you can see and talk with, one that asks follow-up questions, and get feedback on your answers.',
    },
    {
      id: 'hiring', tone: 'success', icon: BusinessCenterOutlinedIcon, who: 'For hiring teams',
      text: 'A structured video interview for every applicant who clears the bar, with a scored report so you can make the decision.',
    },
  ],
};

const ComingSoon = ({ stream }) => (
  <Box
    component="section"
    aria-labelledby="coming-soon-title"
    data-testid="coming-soon"
    sx={{
      py: { xs: 3, md: 4 },
      bgcolor: (theme) => alpha(toneOf(theme, 'warning'), 0.1),
      borderTop: (theme) => `1px solid ${alpha(toneOf(theme, 'warning'), 0.35)}`,
      borderBottom: (theme) => `1px solid ${alpha(toneOf(theme, 'warning'), 0.35)}`,
    }}
  >
    <Container maxWidth="lg">
      <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" justifyContent="center">
          <Chip
            size="small"
            icon={<VideocamOutlinedIcon />}
            label="Coming soon"
            sx={{
              fontWeight: 700,
              bgcolor: (theme) => alpha(toneOf(theme, 'warning'), 0.2),
              border: (theme) => `1px solid ${alpha(toneOf(theme, 'warning'), 0.5)}`,
              '& .MuiChip-icon': { color: (theme) => toneOf(theme, 'warning') },
            }}
          />
          <Typography id="coming-soon-title" variant="h5" component="h2" sx={{ fontWeight: 700 }}>{COMING_SOON.title}</Typography>
        </Stack>
        <Typography color="text.secondary">{COMING_SOON.lead}</Typography>
        <Box sx={{ display: 'grid', gap: 2, width: '100%', gridTemplateColumns: 'minmax(0, 640px)', justifyContent: 'center' }}>
          {COMING_SOON.parts.filter((part) => part.id === stream).map(({ id, tone, icon: Icon, who, text }) => (
            <Box
              key={id}
              data-coming-soon-for={id}
              sx={{
                p: 2, textAlign: 'left', borderRadius: 2, bgcolor: 'background.paper',
                border: (theme) => `1px solid ${alpha(toneOf(theme, tone), 0.45)}`,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <Icon sx={{ color: (theme) => toneOf(theme, tone) }} />
                <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>{who}</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{text}</Typography>
            </Box>
          ))}
        </Box>
      </Stack>
    </Container>
  </Box>
);

/**
 * The front door. `audience` is the stream this address is for (`/for-job-seekers`, `/for-hiring-teams`); at `/` it is left out and
 * the page shows the stream the visitor looked at last, job seekers for somebody new.
 */
const Home = ({ audience }) => {
  const { user, loading } = useAuth();
  const { code: country, price } = useMarket();
  const [pricing, setPricing] = useState(null);
  const [remembered] = useState(rememberedAudience);
  const stream = AUDIENCES[audience] ? audience : (remembered || 'seekers');
  const other = AUDIENCES[stream === 'seekers' ? 'hiring' : 'seekers'];

  // What a visitor looked at is what "/" shows them next time.
  useEffect(() => { if (AUDIENCES[audience]) rememberAudience(audience); }, [audience]);

  useEffect(() => {
    let live = true;
    axios.get('/api/public/signup/pricing')
      .then(({ data }) => { if (live) setPricing(usablePricing(data)); })
      // Prices are the one thing on this page that can be wrong rather than
      // missing, so a failed call leaves them out and the page still reads.
      .catch(() => {});
    return () => { live = false; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Somebody already signed in did not come here to be sold to.
  if (user) return <Navigate to={landingPathFor(user)} replace />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: (theme) => dataflixPalette(theme).navBg,
          color: (theme) => dataflixPalette(theme).navText,
          borderBottom: (theme) => `1px solid ${dataflixPalette(theme).line}`,
        }}
      >
        <Toolbar sx={{ gap: { xs: 0.25, sm: 1 }, px: { xs: 1.5, sm: 3 }, minHeight: { xs: 64, sm: 80 } }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}><Brand /></Box>
          {/* The two streams as one pill: real links to their own pages, the one on screen marked. */}
          <Box
            data-testid="audience-switch"
            sx={{
              display: { xs: 'none', md: 'inline-flex' }, p: 0.5, gap: 0.5, borderRadius: 999,
              border: (theme) => `1px solid ${dataflixPalette(theme).line}`,
            }}
          >
            {AUDIENCE_IDS.map((id) => (
              <Button
                key={id}
                component={RouterLink}
                color="inherit"
                to={AUDIENCES[id].path}
                size="small"
                aria-current={stream === id ? 'page' : undefined}
                sx={{
                  borderRadius: 999, px: 2, whiteSpace: 'nowrap', fontWeight: stream === id ? 700 : 500,
                  bgcolor: (theme) => (stream === id ? alpha(toneOf(theme, AUDIENCES[id].tone), 0.2) : 'transparent'),
                }}
              >
                {AUDIENCES[id].label}
              </Button>
            ))}
          </Box>
          <Button color="inherit" href="#plans" sx={{ display: { xs: 'none', md: 'inline-flex' }, whiteSpace: 'nowrap' }}>Plans</Button>
          <Button color="inherit" href="#faq" sx={{ display: { xs: 'none', md: 'inline-flex' }, whiteSpace: 'nowrap' }}>FAQ</Button>
          <CountrySelect />
          <ThemeModeToggle />
          <BarMenu stream={stream} />
          {/* Three controls do not fit a phone bar. Sign in drops out there —
              the hero offers it again a few hundred pixels below. */}
          <Button
            component={RouterLink}
            to="/login"
            color="inherit"
            sx={{ display: { xs: 'none', sm: 'inline-flex' }, whiteSpace: 'nowrap' }}
          >
            Sign in
          </Button>
          <Button component={RouterLink} to="/signup" variant="contained" sx={{ whiteSpace: 'nowrap', px: { xs: 1.5, sm: 2 }, ml: { xs: 0.5, sm: 0 } }}>
            Get started
          </Button>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flex: 1 }}>
        {/* Hero: a wash of the theme's own colours, so it is colourful in any theme. */}
        <Box
          sx={{
            position: 'relative', overflow: 'hidden',
            background: (theme) => `linear-gradient(135deg, ${alpha(toneOf(theme, 'primary'), 0.16)} 0%, ${alpha(toneOf(theme, 'secondary'), 0.12)} 55%, ${alpha(toneOf(theme, 'success'), 0.1)} 100%)`,
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute', top: -120, right: -80, width: 360, height: 360, borderRadius: '50%',
              bgcolor: (theme) => alpha(toneOf(theme, 'warning'), 0.14), filter: 'blur(8px)',
            }}
          />
          <Container maxWidth="lg" sx={{ position: 'relative', pb: { xs: 4, md: 6 } }}>
            <Box sx={{ pt: { xs: 4, md: 6 }, textAlign: 'center', maxWidth: 780, mx: 'auto' }}>
              <Typography variant="overline" component="p" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.12em' }}>
                Meet {brand.name}
              </Typography>
              <Typography variant="h3" component="h1" sx={{ mt: 0.5, mb: 2, fontWeight: 700, lineHeight: 1.15 }}>
                {brand.tagline}
              </Typography>
              <Typography variant="h6" component="p" color="text.secondary" sx={{ mb: 3, fontWeight: 400, lineHeight: 1.6 }}>
                {HERO_LINE[stream]}
              </Typography>
              {/* How many jobs are open right now, counted up. Shown only once a real count has arrived, and only to job seekers. */}
              {stream === 'seekers' && <OpenJobsCount />}
            </Box>

            {/* The panel for this stream beside RoRi. On a phone: RoRi, then the panel. */}
            <Box
              data-testid="audience-choices"
              sx={{
                display: 'grid', alignItems: 'stretch', gap: { xs: 2, md: 5 },
                gridTemplateAreas: { xs: '"rori" "panel"', md: `"${HERO_AREAS.join(' ')}"` },
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(260px, 340px)' },
              }}
            >
              <AudiencePanel id={stream}>
                {stream === 'seekers' ? (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 'auto', pt: 0.5 }} aria-label={`Ways to use ${brand.name}`}>
                    {[['primary', ChatBubbleOutlineIcon, 'Chat'], ['secondary', MicIcon, 'Voice']].map(([tone, Icon, label]) => (
                      <Chip
                        key={label}
                        size="small"
                        icon={<Icon />}
                        label={label}
                        data-way={label.toLowerCase()}
                        sx={{
                          fontWeight: 700,
                          bgcolor: (theme) => alpha(toneOf(theme, tone), 0.14),
                          border: (theme) => `1px solid ${alpha(toneOf(theme, tone), 0.4)}`,
                          '& .MuiChip-icon': { color: (theme) => toneOf(theme, tone) },
                        }}
                      />
                    ))}
                    <Typography variant="caption" color="text.secondary">Chat is free. Voice is on Premium.</Typography>
                  </Stack>
                ) : null}
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: stream === 'seekers' ? 1 : 'auto', pt: 0.5 }}>
                  <Button component={RouterLink} to={HERO_ACTION[stream].to} variant="contained" color={AUDIENCES[stream].tone}>
                    {HERO_ACTION[stream].label}
                  </Button>
                  {stream === 'hiring' && (
                    <Button component={RouterLink} to="/platform" variant="outlined" color={AUDIENCES[stream].tone}>See the platform</Button>
                  )}
                </Stack>
              </AudiencePanel>
              <RoriStage />
            </Box>

            {/* The other stream, one line away, for somebody who landed on the wrong one. */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" data-testid="other-stream">
                {stream === 'seekers' ? 'Hiring for your team?' : 'Looking for a job?'}{' '}
                <Link component={RouterLink} to={other.path} underline="always">See {other.label.toLowerCase()}</Link>
              </Typography>
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account? <Link component={RouterLink} to="/login" underline="always">Sign in</Link>
              </Typography>
              {stream === 'seekers' && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  A free job seeker account takes about a minute and needs no card.
                </Typography>
              )}
            </Box>
          </Container>
        </Box>

        <ComingSoon stream={stream} />

        <Container maxWidth="lg">
          {/* What it does, for each audience. */}
          <Box id="features" sx={{ scrollMarginTop: 16, pt: { xs: 4, md: 6 }, pb: { xs: 2, md: 4 } }}>
            {stream === 'seekers' ? (
            <AudienceBand
              id="seekers"
              tone="secondary"
              title="Job seekers & students"
              summary={`${brand.name} helps you find a role, track every application and keep the rest of your week straight. Ask it in chat, or on Premium, by voice.`}
              before={<CareerWheel />}
              after={<Box sx={{ textAlign: 'center' }}><PlansLink id="plans-seekers" tone="secondary" label="See the Free and Premium plans" /></Box>}
            >
              {SEEKERS.map((feature) => <FeatureTile key={feature.title} feature={feature} />)}
            </AudienceBand>
            ) : (
            <AudienceBand
              id="hiring"
              tone="success"
              title="Hiring teams and agencies"
              summary={`You make the decisions. ${brand.name}, your AI agent, does the work between them, from requisition to signed offer.`}
              before={<HiringProcess />}
              after={(
                <>
                  <Typography color="text.secondary" sx={{ mt: 2.5, textAlign: 'center', lineHeight: 1.6 }} data-testid="hiring-across">
                    {HIRING_ACROSS}
                  </Typography>
                  <Box sx={{ textAlign: 'center' }}><PlansLink id="plans-hiring" tone="success" label="See the hiring plans" /></Box>
                </>
              )}
            >
              {HIRING_PROCESS.map((step, index) => <StepBox key={step.title} step={step} number={index + 1} features={STEP_FEATURES[index]} />)}
            </AudienceBand>
            )}
          </Box>

          {/* For a hiring team that already has a hiring system and wants only the voice screening. */}
          {stream === 'hiring' && (
            <Box id="existing-system" component="section" aria-labelledby="existing-system-heading" data-testid="existing-system-band" sx={{ scrollMarginTop: 16, mb: { xs: 3, md: 5 } }}>
              <BandHeader id="existing-system" tone="success" title={EXISTING_SYSTEM.title} summary={EXISTING_SYSTEM.summary} />
              <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
                {EXISTING_SYSTEM.steps.map((step, index) => (
                  <Card component="li" key={step.title} variant="outlined" data-existing-step={step.title}>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">Step {index + 1}</Typography>
                      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mb: 0.5 }}>{step.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{step.text}</Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center" sx={{ mt: 2.5 }} role="list" aria-label="Ways to connect">
                {EXISTING_SYSTEM.connects.map((label) => <Chip key={label} role="listitem" label={label} variant="outlined" />)}
              </Stack>
              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
                <Button component={RouterLink} to="/interview-as-a-service" variant="contained" color="success" disableElevation sx={{ mt: 2.5, fontWeight: 700, textTransform: 'none' }}>
                  See Interview as a Service
                </Button>
                <PlansLink id="plans-hiring" tone="success" label="See the plans" />
              </Stack>
            </Box>
          )}

          {/* For a hiring team that has to answer to somebody: what is built in, and where to read more. */}
          {stream === 'hiring' && (
            <Box id="control" component="section" aria-labelledby="control-heading" data-testid="control-band" sx={{ scrollMarginTop: 16, mb: { xs: 3, md: 5 } }}>
              <BandHeader
                id="control"
                tone="success"
                title="Built for control"
                summary="Hiring teams answer for who they hire. These are in the product today, and each links to the page that says more."
              />
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
                {CONTROL_TILES.map((tile) => (
                  <Card key={tile.title} variant="outlined" data-control={tile.title}>
                    <CardContent>
                      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mb: 0.5 }}>{tile.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 1 }}>{tile.text}</Typography>
                      <Link component={RouterLink} to={tile.to} underline="always" variant="body2" aria-label={`${tile.title}: read more`}>Read more</Link>
                    </CardContent>
                  </Card>
                ))}
              </Box>
              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" justifyContent="center" sx={{ mt: 2.5 }} component="nav" aria-label="More for hiring teams">
                {[['/platform', 'The hiring platform'], ['/security', 'Security and trust'], ['/responsible-ai', 'Responsible AI'], ['/industries/financial-services', 'Industries'], ['/contact', 'Talk to us']].map(([to, label]) => (
                  <Link key={to} component={RouterLink} to={to} underline="hover" sx={{ fontWeight: 600 }}>{label}</Link>
                ))}
              </Stack>
              
            </Box>
          )}

          {/* Plans */}
          {/* Plans, in two groups: the ones for students and job seekers, and the one for hiring teams. There is no
              heading over both: the two groups are the headings, in the colours of the sections above. */}
          <Box id="plans" component="section" aria-labelledby="plans-heading" sx={{ scrollMarginTop: 16, mb: { xs: 3, md: 5 } }}>
            <BandHeader id="plans" tone="primary" title="Plans" />
            {PLAN_GROUPS.filter((group) => group.id === stream).map((group) => {
              const GroupIcon = group.icon;
              const plans = PLANS(pricing, price).filter((plan) => plan.group === group.id);
              return (
                <Box
                  key={group.id}
                  id={group.plansId}
                  role="group"
                  aria-labelledby={`${group.plansId}-heading`}
                  data-plan-group={group.id}
                  sx={{
                    scrollMarginTop: 16, mb: 3, p: { xs: 2, md: 3 }, borderRadius: 3,
                    background: (theme) => `linear-gradient(120deg, ${alpha(toneOf(theme, group.tone), 0.18)}, ${alpha(toneOf(theme, group.tone), 0.05)})`,
                    border: (theme) => `1px solid ${alpha(toneOf(theme, group.tone), 0.3)}`,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <Box
                      aria-hidden
                      sx={{
                        width: 44, height: 44, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center',
                        bgcolor: (theme) => alpha(toneOf(theme, group.tone), 0.2), color: (theme) => toneOf(theme, group.tone),
                      }}
                    >
                      <GroupIcon />
                    </Box>
                    <Box>
                      <Typography id={`${group.plansId}-heading`} tabIndex={-1} variant="h6" component="h3" sx={{ fontWeight: 700, lineHeight: 1.25, outline: 'none' }}>
                        {group.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{group.text}</Typography>
                    </Box>
                  </Stack>
                  <Box
                    sx={{
                      display: 'grid', gap: 3,
                      gridTemplateColumns: { xs: '1fr', md: plans.length > 1 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 460px)' },
                    }}
                  >
                    {plans.map((plan) => {
                      const shown = plan.price();
                      return (
                        <Card
                          key={plan.name}
                          variant="outlined"
                          data-plan={plan.name}
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: 'background.paper',
                            borderTop: (theme) => `6px solid ${toneOf(theme, group.tone)}`,
                            ...(plan.highlight ? { borderColor: 'primary.main', borderWidth: 2, borderTopWidth: 6, boxShadow: 4 } : {}),
                          }}
                        >
                          <CardContent sx={{ flex: 1 }}>
                            <Typography variant="overline" component="p" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: (theme) => toneOf(theme, group.tone) }}>
                              {group.title}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <Typography variant="h6" component="h4" sx={{ fontWeight: 700 }}>{plan.name}</Typography>
                              {plan.highlight && <Chip label="Most popular" size="small" color="primary" />}
                            </Stack>
                            {/* No price rather than a guessed one, when pricing did not load. */}
                            {shown && (
                              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
                                <Typography variant="h4" component="p" sx={{ fontWeight: 700 }}>{shown}</Typography>
                                <Typography color="text.secondary">{plan.cadence}</Typography>
                              </Stack>
                            )}
                            <Typography color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>{plan.summary}</Typography>
                            {plan.points?.length ? <Points points={plan.points} /> : null}
                          </CardContent>
                          <Box sx={{ p: 2, pt: 0 }}>
                            <Button
                              component={RouterLink}
                              to={plan.action.to}
                              variant={plan.highlight ? 'contained' : 'outlined'}
                              fullWidth
                            >
                              {plan.action.label}
                            </Button>
                          </Box>
                        </Card>
                      );
                    })}
                  </Box>
                  {/* The detail behind the two personal plans: each part of the Career Wheel, side by side. */}
                  {group.id === 'seekers' && <PlanComparison limits={pricing?.limits} />}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.7 }}>{group.note}</Typography>
                  {group.id === 'hiring' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
                      Questions about security, procurement or volume? <Link component={RouterLink} to="/contact" underline="always">Talk to us</Link>. You do not need to, to start.
                    </Typography>
                  )}
                  {/* Rupees are for reading only: the card is charged in dollars, and the page says so. */}
                  {country === 'IN' && pricing && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }} data-testid="inr-note">{CHARGED_IN_USD}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* What people ask before they sign up. */}
          <Box id="faq" component="section" aria-labelledby="faq-heading" sx={{ scrollMarginTop: 16, mb: { xs: 3, md: 5 } }}>
            <BandHeader id="faq" tone="info" title="Questions people ask" />
            {FAQS(pricing, price).filter((_item, index) => FAQ_STREAM[stream].includes(index)).map((item) => (
              <Accordion key={item.q} variant="outlined" disableGutters sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>{item.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>

        {/* Closing call to action, full width and in colour. */}
        <Box
          sx={{
            py: { xs: 5, md: 7 }, textAlign: 'center',
            background: (theme) => `linear-gradient(120deg, ${alpha(toneOf(theme, 'primary'), 0.2)}, ${alpha(toneOf(theme, 'secondary'), 0.16)} 60%, ${alpha(toneOf(theme, 'info'), 0.14)})`,
          }}
        >
          <Container maxWidth="sm">
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {stream === 'seekers'
                ? 'Create a free account and add your first task. Upgrade only if you want more done for you.'
                : 'Set up your organization, open your first requisition, and let the agent do the work between your decisions.'}
            </Typography>
            <Button component={RouterLink} to={HERO_ACTION[stream].to} variant="contained" size="large">{HERO_ACTION[stream].label}</Button>
          </Container>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default Home;
